import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Box,
  Typography,
  Alert,
  Chip,
  TextField,
  CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { SelectedManifestSSCC } from '../utils/types';

interface DeleteSSCCDialogProps {
  open: boolean;
  selectedManifest: SelectedManifestSSCC | null;
  actionReason: string;
  isProcessing: boolean;
  onClose: () => void;
  onReasonChange: (reason: string) => void;
  onConfirm: () => void;
}

export default function DeleteSSCCDialog({
  open,
  selectedManifest,
  actionReason,
  isProcessing,
  onClose,
  onReasonChange,
  onConfirm
}: DeleteSSCCDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}>
      <DialogTitle
        sx={{
          borderBottom: '1px solid rgba(0,0,0,0.08)',
          px: 3,
          py: 2.5,
          backgroundColor: '#fef6f6'
        }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              p: 1,
              borderRadius: 1.5,
              backgroundColor: 'background.default',
              display: 'flex',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
            }}>
            <DeleteIcon sx={{ fontSize: 22, color: '#d32f2f' }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Delete SSCC
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Permanently remove this SSCC
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 3, '& > :first-of-type': { mt: 1 } }}>
        <Stack spacing={3}>
          <Alert
            severity="warning"
            icon={<ErrorOutlineIcon />}
            sx={{
              borderRadius: 1.5,
              border: '1px solid rgba(237, 108, 2, 0.2)'
            }}>
            This action will permanently mark the SSCC as deleted from the manifest. This action cannot be undone.
          </Alert>

          <Box
            sx={{
              p: 2.5,
              borderRadius: 1.5,
              backgroundColor: '#fafafa',
              border: '1px solid rgba(0,0,0,0.08)'
            }}>
            <Stack spacing={2}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: 'text.secondary'
                  }}>
                  SSCC
                </Typography>
                <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                  {selectedManifest?.ssccId}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: 'text.secondary'
                  }}>
                  Current Status
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip label={selectedManifest?.ssccStatus} size="small" />
                </Box>
              </Box>
            </Stack>
          </Box>

          <Box>
            <Typography
              variant="caption"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                fontSize: '0.7rem',
                fontWeight: 600,
                color: 'text.secondary',
                mb: 1.5,
                display: 'block'
              }}>
              Reason for Deletion *
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              required
              value={actionReason}
              onChange={(e) => onReasonChange(e.target.value)}
              placeholder="Explain why this SSCC is being deleted (e.g., duplicate, erroneous entry, etc.)..."
              helperText="This reason will be logged in the audit trail"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  fontSize: '0.85rem'
                }
              }}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2.5,
          backgroundColor: '#fafafa',
          borderTop: '1px solid rgba(0,0,0,0.08)'
        }}>
        <Button
          onClick={onClose}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            color: 'text.secondary',
            fontSize: '0.9rem'
          }}>
          Cancel
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={onConfirm}
          disabled={!actionReason.trim() || isProcessing}
          startIcon={
            isProcessing ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon sx={{ fontSize: 16 }} />
          }
          sx={{
            px: 2,
            py: 0.75,
            fontWeight: 500,
            fontSize: '0.8rem',
            textTransform: 'none',
            borderColor: 'rgba(211, 47, 47, 0.5)',
            color: '#d32f2f',
            borderRadius: 1,
            '&:hover': {
              borderColor: '#d32f2f',
              backgroundColor: 'rgba(211, 47, 47, 0.04)'
            }
          }}>
          {isProcessing ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
